import { Injectable, inject } from '@angular/core';
import { Query, toBoolean } from '@datorama/akita';
import { map, tap, concatMap, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { IntegratedLoginContext, SessionModel } from './session.models';
import { SessionStore } from './session.store';
import { isLoanSaveAllowed, softLockStatuses } from './session.util';
import { MsaCounties } from '../../../cmg-1003/models';
import { HttpClient } from '@angular/common/http';
import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';
import { Models } from '../../../models';
import { CurrentUserService } from '../../../shared';

@Injectable({ providedIn: 'root' })
export class SessionQuery extends Query<SessionModel> {
  protected _sessionStore: SessionStore;
  private readonly _httpClient = inject(HttpClient);
  private readonly _currentUserService = inject(CurrentUserService);
  private readonly _environment = inject(APP_ENV);

  isLoggedIn$ = this.select((state) => toBoolean(state.token));

  /** Determines whether there are currently any validation errors (red). */
  hasValidationErrors$ = this.select().pipe(map((session) => session.validationErrors.length > 0));

  /** Determines whether there are currently any validation warnings (yellow). */
  hasValidationWarnings$ = this.select().pipe(
    map((session) => {
      let hasValidations = false;

      if (session.validationWarnings && session.validationWarnings.length > 0) {
        for (let i = 0; i < session.validationWarnings.length; i++) {
          if (session.validationWarnings[i].invalidControls && session.validationWarnings[i].invalidControls.length > 0) {
            hasValidations = true;
            break;
          }
        }
      }

      return hasValidations;
    }),
  );

  /** Determines whether there are any validation failures - warnings, errors. */
  hasValidationFailures$ = this.select().pipe(map((session) => session.validationErrors.length > 0 || session.validationWarnings.length > 0));

  sessionStatus$ = this.select((state) => state.loanStatusId);

  isAusEnabled$ = this.select((t) => softLockStatuses.includes(t.loanStatusId));

  hasAusPanelBeenOpened$ = this.select().pipe(map((session) => session.hasAusPanelBeenOpened));

  lastRefreshDateTime$ = this.select((state) => state.lastRefreshDateTime);

  isLoanSaveAllowed$ = this.select((t) => t.loanStatusId).pipe(map((loanStatusId) => isLoanSaveAllowed(loanStatusId)));

  isIntegratedLogin$ = this.select((s) => s.integratedLoginContext === IntegratedLoginContext.Byte);

  constructor() {
    const _sessionStore = inject(SessionStore);

    super(_sessionStore);

    this._sessionStore = _sessionStore;
  }

  initUserSettings(value: Models.UserSettings): void {
    this._sessionStore.update({ userSettings: value });
  }

  getUserSettings() {
    return this.select((state) => state.userSettings);
  }

  /**
   * Update the user settings store and save to the back-end
   * @param newSettings
   */
  updateUserSettings(newSettings: Partial<Models.UserSettings>): void {
    this.select((state) => state.userSettings)
      .pipe(take(1))
      .subscribe((oldSettings) => {
        const updatedSettings = {
          ...oldSettings,
          ...newSettings,
        } as Models.UserSettings;

        this.initUserSettings(updatedSettings);
        this._currentUserService.updateUserSettings(updatedSettings);
      });
  }

  updateLayoutPageSizeSetting(layoutGuid: string, pageSize: number): void {
    this.select((state) => state.userSettings)
      .pipe(take(1))
      .subscribe((settings) => {
        let setting = settings ? settings['layoutPageSizes'] : {};
        if (setting) {
          setting[layoutGuid] = pageSize;
        } else {
          setting = { [layoutGuid]: pageSize };
        }

        if (settings) {
          settings['layoutPageSizes'] = setting;
        } else {
          settings = { layoutPageSizes: setting };
        }

        this.initUserSettings(settings);
        this._currentUserService.updateUserSettings(settings);
      });
  }

  getMsaCountiesBystate(state: string): Observable<MsaCounties[]> {
    return this.select(({ msaCounties }) => {
      if (msaCounties?.[state]) {
        return msaCounties?.[state];
      }

      return null;
    }).pipe(
      take(1),
      concatMap((val) => {
        if (!val) {
          return this._getMsaCounties(state).pipe(
            tap((counties) => {
              this._sessionStore.update((store) => ({
                ...store,
                msaCounties: {
                  ...store.msaCounties,
                  [state]: counties,
                },
              }));
            }),
          );
        }

        return of(val);
      }),
    );
  }

  private _getMsaCounties(stateAbbreviation: string): Observable<MsaCounties[]> {
    return this._httpClient.get<MsaCounties[]>(`${this._environment.retail.apiUrl}/aus/msacounties?stateAbbreviation=${stateAbbreviation}`);
  }
}
